import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type {
  Concept,
  ConceptCreate,
  ConceptListItem,
  ConceptUpdate,
  ConceptVersion,
} from "@/types/concept";

export function useConceptList() {
  return useQuery<ConceptListItem[]>({
    queryKey: ["concepts"],
    queryFn: () => apiClient.get("api/concepts").json(),
  });
}

export function useConcept(id: string | undefined) {
  // Reactive (re-renders when facet ops start/finish), so the poll timer is
  // re-armed once they settle — qc.isMutating() inside the callback is not.
  const facetBusy = useIsMutating({ mutationKey: ["facets", id] }) > 0;
  return useQuery<Concept>({
    queryKey: ["concepts", id],
    queryFn: () => apiClient.get(`api/concepts/${id}`).json(),
    enabled: !!id,
    // Facets 1c-2: poll while an angle rescore pass is queued/running.
    // Absent on old backends -> false -> no polling.
    // Skip while a facet op runs so a poll can't clobber its optimistic write.
    // Inlined key = facetMutationKey(id) in useFacets.ts (importing it would cycle).
    refetchInterval: (q) =>
      q.state.data?.angle_rescore_pending && !facetBusy ? 30_000 : false,
  });
}

export function useCreateConcept() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConceptCreate) =>
      apiClient.post("api/concepts", { json: data, timeout: 120_000 }).json<Concept>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
    },
  });
}

export function useUpdateConcept(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConceptUpdate & { skip_version?: boolean }) => {
      const { skip_version, ...body } = data;
      const params = skip_version ? "?skip_version=true" : "";
      return apiClient.patch(`api/concepts/${id}${params}`, { json: body }).json<Concept>();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts", id], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["concepts"], exact: true });
    },
  });
}

/** "Looks right" on a drafted research question. Optimistic per house rule 8. */
export function useConfirmDraftedQuestion(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.patch(`api/concepts/${id}`, { json: { abstract_auto_drafted: false } }).json<Concept>(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["concepts", id] });
      const previous = queryClient.getQueryData<Concept>(["concepts", id]);
      if (previous) {
        queryClient.setQueryData<Concept>(["concepts", id], { ...previous, abstract_auto_drafted: false });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["concepts", id], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts", id] });
    },
  });
}

export function useConceptHistory(id: string | undefined) {
  return useQuery<ConceptVersion[]>({
    queryKey: ["concepts", id, "history"],
    queryFn: () => apiClient.get(`api/concepts/${id}/history`).json(),
    enabled: !!id,
  });
}

export function useDeleteConceptVersion(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiClient.delete(`api/concepts/${conceptId}/history/${versionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts", conceptId, "history"], refetchType: "all" });
    },
  });
}

export function useClearConceptHistory(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.delete(`api/concepts/${conceptId}/history`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["concepts", conceptId, "history"] });
      const previous = queryClient.getQueryData<ConceptVersion[]>(["concepts", conceptId, "history"]);
      // Optimistically keep only the latest version
      if (previous && previous.length > 0) {
        queryClient.setQueryData<ConceptVersion[]>(
          ["concepts", conceptId, "history"],
          [previous[0]],
        );
      }
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["concepts", conceptId, "history"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts", conceptId, "history"] });
    },
  });
}

export function useDeleteConcept() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`api/concepts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
    },
  });
}

export function useRefreshSuggestedQuestions(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient
        .post(`api/concepts/${conceptId}/suggested-questions/refresh`, { timeout: 120_000 })
        .json<{ questions: { text: string; source: string }[] }>(),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts", conceptId] });
    },
  });
}
